import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import { usePrefetchMaxTagsQuery } from '#api/tags.queries.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useUrlParam } from '#hooks/useUrlParam.ts';
import { NotFound } from '#screens/404/NotFound.tsx';
import { fallbackRenderNotFound } from '#screens/errors/fallbackRenderNotFound.tsx';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { DuplicateRoleForm } from '../duplicate/DuplicateRoleForm.tsx';
import { RoleDetailForm } from '../form/RoleDetailForm.tsx';

export function AddRolePage() {
  const location = useAppLocation();
  const navigate = useNavigate();

  // When set, prefill the form with a copy of this role's config
  const [duplicateFrom] = useUrlParam('duplicateFrom');
  const isDuplicating = duplicateFrom !== undefined;

  const pageName = isDuplicating ? 'Duplicate' : 'Add';

  {
    // prefetch for InlineTagsList
    usePrefetchMaxTagsQuery();
  }

  function onFormSaved() {
    // Return to the list view after saving the role
    void navigate(location.state?.from ?? '/roles', { replace: true });
  }

  // A present-but-empty duplicateFrom is a broken link, not a plain add
  if (isDuplicating && !duplicateFrom) {
    return <NotFound />;
  }

  return (
    <Titled title={(title) => fromArray([pageName, title])}>
      <ErrorBoundary fallbackRender={fallbackRenderNotFound}>
        <MainLayout
          header={
            <PageHeader
              title={
                <>
                  <Link to="/roles" className="a">
                    Roles
                  </Link>
                  {' / '}
                  {pageName}
                </>
              }
            >
              <PageCloser fallbackTo="/roles" />
            </PageHeader>
          }
        >
          {duplicateFrom ?
            <Suspense fallback={<LoadingSpinner />}>
              <DuplicateRoleForm sourceRoleId={duplicateFrom} onSave={onFormSaved} />
            </Suspense>
          : <RoleDetailForm roleId={null} onSave={onFormSaved} />}
        </MainLayout>
      </ErrorBoundary>
    </Titled>
  );
}
